import React, { useState, useMemo } from 'react';
import styles from './DataTable.module.css';
import { toast } from 'react-toastify';

const DataTable = ({
  data = [],
  columns = [],
  renderActions,
  currentPage = 1,
  totalPages = 1,
  rowsPerPage = 10,
  totalItems = 0,
  onPageChange,
  onRowsPerPageChange,
  sortable = true,
  handleRow
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

   const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle different data types
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Handle dates (for scheduledDate)
      if (sortConfig.key === 'scheduledDate' && a.rawScheduledDate && b.rawScheduledDate) {
        const comparison = new Date(a.rawScheduledDate) - new Date(b.rawScheduledDate);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle numbers (for participants like "1/7")
      if (sortConfig.key === 'participants') {
        const aNum = parseInt(aValue) || 0;
        const bNum = parseInt(bValue) || 0;
        const comparison = aNum - bNum;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const comparison = aValue - bValue;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Default string comparison
      const comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);


  const handleSort = (columnKey) => {
    if (!sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const getSortIcon = (columnKey) => {
  if (!sortable) return null;
  if (sortConfig.key !== columnKey) {
    return <i className={`bi bi-funnel-fill ${styles.sortIcon}`}></i>;
  }
  return sortConfig.direction === 'asc' 
    ? <i className={`bi bi-caret-up-fill ${styles.sortIcon} ${styles.active}`}></i>
    : <i className={`bi bi-caret-down-fill ${styles.sortIcon} ${styles.active}`}></i>;
};

  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    onRowsPerPageChange(newRowsPerPage);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const renderPaginationNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`${styles.paginationNumber} ${i === currentPage ? styles.active : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    return pages;
  };

  const startItem = ((currentPage - 1) * rowsPerPage) + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

const handleCopyLink = (url) => {
  navigator.clipboard.writeText(url).then(() => {
    toast.success('Link copied to clipboard!', {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }).catch(err => {
    console.error('Failed to copy: ', err);
    toast.error('Failed to copy link', {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  });
};

  return (
    <div className={styles.datatableContainer}>
      <div className={styles.datatableWrapper}>
        <table className={styles.datatable}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  className={`${styles.datatableHeader} ${column.sortable !== false && sortable ? styles.sortable : ''} ${column.headerClassName ? styles[column.headerClassName] : ''}`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className={styles.headerContent}>
                    <span>{column.label}</span>
                    {column.sortable !== false && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.noData}>
                  No data available
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <tr key={row.id || index} className={styles.datatableRow}>
                  {columns.map((column) => (
                    <td key={column.key} className={styles.datatableCell}>
                     
                      {column.key === 'actions' && renderActions 
                        ? renderActions(row)
                        :column?.isRow ? (
                          handleRow ?handleRow({
                            ...row,
                            key:column.key,
                            value: row[column.key]
                          }) : row[column.key]
                        )                      
                        : column.key === 'sessionName'
                        ? (
                          <div className={styles.sessionNameContainer}>
                            <span className={styles.sessionNameText}>{row[column.key]}</span>
                            {row.sessionLink && (
                              <div className={styles.sessionLinkContainer}>
                                {/* <a 
                                  href={row.sessionLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.sessionLink}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Class Link
                                </a>
                                <i 
                                  className={`bi bi-copy ${styles.copyIcon}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyLink(row.sessionLink);
                                  }}
                                  title="Copy link to clipboard"
                                ></i> */}
                              </div>
                            )}
                          </div>
                        )
                        : column.key === 'sessionType'
                        ? (
                          <div className={styles.sessionTypeContainer}>
                            <span className={`${styles.sessionTypeBadge} ${styles[row[column.key]]}`}>
                              {row[column.key] === 'private' ? 'Private' : 'Group'}
                            </span>
                          </div>
                        )
                        : row[column.key]
                      }
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.datatableFooter}>
        <div className={styles.rowsPerPage}>
          <label>
            Rows per page:
            <select 
              value={rowsPerPage} 
              onChange={handleRowsPerPageChange}
              className={styles.rowsSelect}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>

        <div className={styles.paginationInfo}>
          {startItem}-{endItem} of {totalItems}
        </div>

        <div className={styles.paginationControls}>
          <button
            className={styles.paginationBtn}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            title="First page"
          >
            <i className="bi bi-chevron-double-left"></i>
          </button>
          <button
            className={styles.paginationBtn}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            title="Previous page"
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          
          {renderPaginationNumbers()}
          
          <button
            className={styles.paginationBtn}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Next page"
          >
            <i className="bi bi-chevron-right"></i>
          </button>
          <button
            className={styles.paginationBtn}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            title="Last page"
          >
            <i className="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;